/**
 * The monitoring overview's query hooks, one per CO-5 route. Each takes an explicit `{ from, to }`
 * window rather than computing "now" itself, so a story or a test can pin one and every call in
 * one render agrees on what "today" means.
 */

import { useQuery, type UseQueryResult } from '@tanstack/react-query';

import type { ApiFailureError } from '../../../../lib/http/errors';
import { useApiClient } from '../../../session/client';
import { type Agenda, type AudioTrack, type CollectionSummary, type Failure } from './schemas';
import { GET_ROUTES, routePath } from '../../../../lib/http/routes';

/** ISO instants, half-open `[from, to)`, matching the data area's own `Window`. */
export interface Window {
  from: string;
  to: string;
}

function windowParams(window: Window): URLSearchParams {
  return new URLSearchParams({ from: window.from, to: window.to });
}

export const AGENDA_QUERY_KEY = (campaignId: string, window: Window) =>
  ['campaign', campaignId, 'agenda', window] as const;
export const FAILURES_QUERY_KEY = (campaignId: string, window: Window) =>
  ['campaign', campaignId, 'failures', window] as const;
export const COLLECTION_SUMMARY_QUERY_KEY = (campaignId: string, window: Window) =>
  ['campaign', campaignId, 'collection-summary', window] as const;
export const AUDIO_QUERY_KEY = (campaignId: string) => ['campaign', campaignId, 'audio'] as const;

export function useAgenda(
  campaignId: string,
  window: Window,
): UseQueryResult<Agenda, ApiFailureError> {
  const client = useApiClient();
  return useQuery<Agenda, ApiFailureError>({
    queryKey: AGENDA_QUERY_KEY(campaignId, window),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.campaignAgenda, { campaignId }, windowParams(window)),
        GET_ROUTES.campaignAgenda.schema,
      ),
    enabled: campaignId !== '',
  });
}

export function useFailures(
  campaignId: string,
  window: Window,
): UseQueryResult<Failure[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<Failure[], ApiFailureError>({
    queryKey: FAILURES_QUERY_KEY(campaignId, window),
    queryFn: async () => {
      const { failures } = await client.getParsed(
        routePath(GET_ROUTES.campaignFailures, { campaignId }, windowParams(window)),
        GET_ROUTES.campaignFailures.schema,
      );
      return failures;
    },
    enabled: campaignId !== '',
  });
}

export function useAudio(campaignId: string): UseQueryResult<AudioTrack[], ApiFailureError> {
  const client = useApiClient();
  return useQuery<AudioTrack[], ApiFailureError>({
    queryKey: AUDIO_QUERY_KEY(campaignId),
    queryFn: async () => {
      const { tracks } = await client.getParsed(
        routePath(GET_ROUTES.campaignAudio, { campaignId }),
        GET_ROUTES.campaignAudio.schema,
      );
      return tracks;
    },
    enabled: campaignId !== '',
  });
}

export function useCollectionSummary(
  campaignId: string,
  window: Window,
): UseQueryResult<CollectionSummary, ApiFailureError> {
  const client = useApiClient();
  return useQuery<CollectionSummary, ApiFailureError>({
    queryKey: COLLECTION_SUMMARY_QUERY_KEY(campaignId, window),
    queryFn: () =>
      client.getParsed(
        routePath(GET_ROUTES.campaignCollectionSummary, { campaignId }, windowParams(window)),
        GET_ROUTES.campaignCollectionSummary.schema,
      ),
    enabled: campaignId !== '',
  });
}
